// Package 4, part A (owner, 7. 10. 2026; docs/PAKET4_A_prvih7dana.md): a new account's systems open one by one.
// 10. 10. 2026 (owner, deploy7): ONLY by the survivor's level, the day plays no part (no day, no session, no catch-up).
// Several systems on one level open together and are all usable at once; each has a small popup (`card`: its name, what
// it is and what is done there; `Try it` opens it, `Later` puts it away), shown one at a time, and a dot on its button
// until the player first opens it. Only the first session is guided (guided.ts). Only accounts made after the package 4
// deploy have this; the rest keep the rules by level (unlocks.ts). Zones and run durations stay with the level too.

import type { FeatureId } from './unlocks';

export const SCHEDULE_RULES = {
  on: true,
  /** The server's time zone for the account's day (analytics only since 10. 10. 2026). */
  timeZone: 'Europe/Sarajevo',
};

export type ScheduleId =
  | 'contracts'
  | 'season'
  | 'craft'
  | 'meals'
  | 'retreat'
  | 'buildings'
  | 'trader'
  | 'upgrade'
  | 'truck'
  | 'generator'
  | 'horde'
  | 'skills'
  | 'stances'
  | 'tradeLists'
  | 'dismantleBulk'
  | 'appearance'
  | 'longRuns'
  | 'molotov'
  | 'radio'
  | 'collection'
  | 'nightRun'
  | 'respec'
  | 'crews'
  // 11. 10. 2026 (owner, docs/thepit.md): The Pit at level 10 (behind the server's FEATURE_PIT: `needs: 'pit'`).
  | 'pit'
  // 11. 10. 2026 (owner, docs/marketplace.md): the Market at level 10 (its flag and a week-old account: `needs: 'market'`).
  | 'market';

/** The popup of a system: its name, two or three sentences (what it is, what is done there) and where `Try it` goes. */
export interface SystemCard {
  title: string;
  text: string;
  to: string;
}

export interface ScheduleRow {
  id: ScheduleId;
  /** 10. 10. 2026 (owner): the survivor's level that opens it. */
  level: number;
  /** The systems it opens (`unlocks.ts`); none: the popup only (long runs and the night run are content). */
  features: FeatureId[];
  card: SystemCard;
  /**
   * Waits for a part of the game that is not there yet (the radio, part L; The Pit while its flag is off): skipped until
   * it is. 11. 10. 2026: the Market waits for its flag and an account older than 7 days (`market`, decided per survivor
   * by the server). deploy8: crews wait for the server's `groups` flag (`crews`; off on the server, owner 11. 10. 2026).
   */
  needs?: 'radio' | 'pit' | 'market' | 'crews';
  /** 11. 10. 2026 (BACKLOG #33): it also opens after this many days of the account (crews: the first week). */
  orDays?: number;
}

/** 10. 10. 2026 (owner's list): level 2 the board and the season path, 3 the Workshop, 4 the camp, 5 the horde, … */
export const SCHEDULE: ScheduleRow[] = [
  {
    id: 'contracts',
    level: 2,
    features: ['contracts', 'ads'],
    card: { title: 'Contracts', text: 'Three jobs a day, from easy to hard. Your runs fill them by themselves: claim each one for supplies, and all three for a bonus. Your first one is already on the board.', to: '/camp#camp-contracts' },
  },
  { id: 'season', level: 2, features: ['season'], card: { title: 'Season path', text: 'Goals for the whole season, each with its reward. Runs, contracts and the horde fill them as you play; claim them on the season screen.', to: '/season' } },
  {
    id: 'craft',
    level: 3,
    features: ['craft'],
    card: { title: 'Workshop', text: "Make what you can't find: Bandages, food, tools and gear, from the materials you bring home. A job takes real time and goes on while you are away. Repair and dismantle your gear from its card in the inventory.", to: '/workshop' },
  },
  { id: 'meals', level: 3, features: ['meals'], card: { title: 'Meals', text: 'Cook at the Workshop and eat before a run. A meal works for that one run, and each dish helps in its own way.', to: '/workshop?tab=supplies' } },
  { id: 'retreat', level: 3, features: ['retreat'], card: { title: 'Retreat', text: "In the run's orders, choose when your survivor turns back: hurt, or with a full pack. Coming home early keeps the loot safe; pushing on finds more.", to: '/map' } },
  { id: 'buildings', level: 4, features: ['buildings'], card: { title: 'Buildings', text: 'Station 9 can grow: each upgrade gives more room, faster work or a better defense. Start with Storage. Upgrades cost materials and take real time.', to: '/camp' } },
  { id: 'trader', level: 4, features: ['trader'], card: { title: 'Tobias', text: 'Tobias trades at the gate. He buys what you have too much of and sells what you are short of, and his prices move. He always haggles.', to: '/trader' } },
  { id: 'upgrade', level: 4, features: ['upgrade'], card: { title: 'Upgrades', text: 'Make your best gear stronger: each upgrade raises its stats and costs materials. Open an item in your inventory to upgrade it.', to: '/inventory' } },
  { id: 'truck', level: 4, features: ['truck'], card: { title: 'The truck', text: "On a run of twenty minutes or more, take the truck: it brings more home and burns Fuel on the way. Switch it on in the run's orders.", to: '/map' } },
  { id: 'generator', level: 4, features: ['generator'], card: { title: 'Generator', text: 'With the generator on, the Workshop works faster. It takes its Fuel from Storage once a day; without Fuel it just stands still.', to: '/camp' } },
  {
    id: 'horde',
    level: 5,
    features: ['hordeJoin'],
    card: { title: 'The horde', text: 'At 19:00 the siren calls the Infected. Fight their waves together with everyone, or stand guard if you will be away. Light the fire barrels in the hour before, and bring Ammo or Fuel for your weapon.', to: '/horde' },
  },
  { id: 'skills', level: 5, features: ['skills'], card: { title: 'Skill tree', text: 'Your skill points are waiting. Pick a path and learn skills that change how your survivor fights and finds.', to: '/skills' } },
  { id: 'stances', level: 5, features: ['shadow'], card: { title: 'Shadow stance', text: "A new stance: follow other survivors unseen and strike from hiding. Pick it in the run's orders.", to: '/map' } },
  // 10. 10. 2026: not on the owner's list; with the stances (both are about meeting other survivors).
  { id: 'tradeLists', level: 5, features: ['tradeLists'], card: { title: 'Trade lists', text: 'Say what you offer and what you want before a run. Meet someone who wants the same, and you trade on the spot.', to: '/map' } },
  { id: 'dismantleBulk', level: 6, features: ['dismantleBulk'], card: { title: 'Dismantle many', text: 'Clear out old gear in one go: pick several items in your inventory and break them down together.', to: '/inventory' } },
  { id: 'appearance', level: 6, features: ['appearance'], card: { title: 'Appearance', text: 'Wear the outfits you unlock as you collect. They change how your survivor looks, nothing else.', to: '/survivor/appearance' } },
  { id: 'longRuns', level: 6, features: [], card: { title: 'Long runs', text: 'Runs of two hours and more find more and let your survivor rest on the quiet stretches. Send one before a long break.', to: '/map' } },
  { id: 'molotov', level: 6, features: ['molotov'], card: { title: 'Molotov', text: 'Craft Molotovs at the Workshop from Fuel and Cloth. Thrown at the start of a fight, one sets the enemy burning for a few rounds.', to: '/workshop?tab=supplies' } },
  { id: 'radio', level: 7, features: ['radio'], card: { title: 'Radio', text: 'Danny Dawn is on the air at 8:00 and 18:30: news from the zones, tips, and now and then a rumor worth chasing.', to: '/camp' }, needs: 'radio' },
  // 13. 10. 2026 (owner): the Collection book is open from the start, no level and no popup ("nema potrebe zaključavati");
  // `collection` stays a ScheduleId for the accounts that have it in `opened`.
  { id: 'nightRun', level: 8, features: [], card: { title: 'Night run', text: 'Before you sleep, send a run of two hours or more. Your survivor comes home while you rest.', to: '/map' } },
  { id: 'respec', level: 8, features: ['respec'], card: { title: 'Respec', text: 'Move your attribute points and reset the skill tree, to try another path.', to: '/survivor' } },
  // 11. 10. 2026 (owner, BACKLOG #33): crews at level 8, or after the account's first week (`orDays`).
  {
    id: 'crews',
    level: 8,
    orDays: 7,
    needs: 'crews',
    features: ['crews'],
    card: { title: 'Crews', text: 'Up to five survivors under one flag, with a chat, a weekly contract and the horde together. Join an open crew in one tap, or found your own from level 10.', to: '/crew' },
  },
  // 11. 10. 2026 (owner, docs/thepit.md): the arena; it waits while the server's flag is off (`SCHEDULE_PARTS.pit`).
  {
    id: 'pit',
    level: 10,
    features: ['pit'],
    card: { title: 'The Pit', text: 'The Foundry runs fights at the Steelworks. Sign up, pick a style and beat other survivors for Scrap, materials and a place in your league. You lose no gear there.', to: '/pit' },
    needs: 'pit',
  },
  // 11. 10. 2026 (owner, docs/marketplace.md): the Market on level 10, with an account older than 7 days and its flag on.
  {
    id: 'market',
    level: 10,
    features: ['market'],
    card: { title: 'Market', text: 'Buy and sell gear and Blueprints with other survivors, for Relic Shards. You set the price and the first buyer gets it. A tenth of every sale is lost to the tax.', to: '/market' },
    needs: 'market',
  },
];

/**
 * 10. 10. 2026 (owner): the popups of things that come when they are needed (they were guided steps): the first worn
 * item, the first Blueprint and Relic Shards (both wait for the Workshop: `waits`), an item ready for a tier or a rework.
 */
export type NeedCardId = 'repair' | 'blueprint' | 'relicForge' | 'tierUp' | 'rework';

export const NEED_CARDS: Record<NeedCardId, { card: SystemCard; waits?: FeatureId }> = {
  repair: { card: { title: 'Repairs', text: 'Your gear wears down on runs, and a broken item stops working. Repair it from its card in your inventory before it breaks.', to: '/inventory' } },
  blueprint: { card: { title: 'Blueprint', text: 'You found a Blueprint. Open it in your inventory, under Special, and learn one of its recipes for the Workshop.', to: '/inventory' }, waits: 'craft' },
  relicForge: { card: { title: 'Relic Forge', text: 'Relic Shards become a Legendary at the Workshop. Roll candidates and keep the one you like.', to: '/workshop?tab=relic' }, waits: 'craft' },
  tierUp: { card: { title: 'Tier up', text: 'One of your items can go up a tier: stronger base stats, at the Workshop. Open it in your inventory.', to: '/inventory' } },
  rework: { card: { title: 'Rework', text: 'One of your items is at its highest upgrade. Rework it into a higher rarity: open it in your inventory.', to: '/inventory' } },
};

/** A popup's id: a system the level opened, or a thing needed now. */
export type CardId = ScheduleId | NeedCardId;

/**
 * Parts of the game the schedule waits for (the radio came with part L: on the server since 9. 10. 2026). 11. 10. 2026:
 * The Pit while the server's FEATURE_PIT is off (`setPitOn`); the Market's part is the survivor's own (the flag and the
 * account's age): the server passes it to `scheduleOpenNow`. deploy8: the crews' part is the server's `groups` flag (on by
 * default, like the flag; the server passes it).
 */
export const SCHEDULE_PARTS: Record<NonNullable<ScheduleRow['needs']>, boolean> = { radio: true, pit: false, market: false, crews: true };

/** Features the schedule opens: for an account with a schedule they open only by it. */
export const SCHEDULED_FEATURES: ReadonlySet<FeatureId> = new Set(SCHEDULE.flatMap((r) => r.features));

/**
 * A popup: queued when its system opened (or its need came), shown one at a time; gone once tried, put away (`closed`:
 * Later) or ignored (the player went to another screen while it was up).
 */
export interface ScheduleCard {
  id: CardId;
  at: number;
  shown?: number;
  tried?: number;
  closed?: number;
  ignored?: number;
}

export interface ScheduleState {
  /** Systems opened so far, in order, with when. */
  opened: ScheduleId[];
  openedAt: Partial<Record<ScheduleId, number>>;
  cards: ScheduleCard[];
  /** Sessions counted since the account was made (the first is the one it was made in). */
  sessions: number;
}

export const newSchedule = (): ScheduleState => ({ opened: [], openedAt: {}, cards: [], sessions: 1 });

/** The calendar date in the server's time zone ("2026-10-07"). */
function localDate(ms: number, timeZone = SCHEDULE_RULES.timeZone): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(ms));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '01';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** The account's day: 1 until the first midnight (server time) after it was made, then one more every midnight (analytics). */
export function accountDay(createdAt: number, now: number): number {
  const a = Date.parse(`${localDate(createdAt)}T00:00:00Z`);
  const b = Date.parse(`${localDate(now)}T00:00:00Z`);
  return Math.max(1, Math.round((b - a) / 86_400_000) + 1);
}

/**
 * 10. 10. 2026 (owner): what opens now: every system of the survivor's level or below not open yet (a missing part waits).
 * 11. 10. 2026 (BACKLOG #33): `days` (the account's whole days) also opens a row with `orDays` (crews after the first
 * week); `parts`: the parts there for this survivor (the Market's).
 */
export function scheduleOpenNow(state: Pick<ScheduleState, 'opened'>, level: number, days = 0, parts: Partial<typeof SCHEDULE_PARTS> = {}): ScheduleRow[] {
  const has = { ...SCHEDULE_PARTS, ...parts };
  return SCHEDULE.filter((r) => (r.level <= level || (r.orDays != null && days >= r.orDays)) && !state.opened.includes(r.id) && (!r.needs || has[r.needs]));
}

export const scheduleRow = (id: ScheduleId) => SCHEDULE.find((r) => r.id === id)!;

/** The popup of a system or a need. */
export const cardDef = (id: CardId): SystemCard => (id in NEED_CARDS ? NEED_CARDS[id as NeedCardId].card : scheduleRow(id as ScheduleId).card);

/** The features open by the schedule so far. */
export function scheduledOpen(state: Pick<ScheduleState, 'opened'>): FeatureId[] {
  return state.opened.flatMap((id) => scheduleRow(id)?.features ?? []);
}

/** 10. 10. 2026: the level a feature opens at on a schedule ("Opens at level 4"), or null when the schedule does not open it. */
export function scheduleOpensAt(feature: FeatureId): { level: number } | null {
  const r = SCHEDULE.find((x) => x.features.includes(feature));
  return r ? { level: r.level } : null;
}

/**
 * The popups still waiting (not tried, put away or ignored), oldest first: the client shows the first. A need's popup
 * waits for its system (`waits`: the Workshop for a Blueprint or Relic Shards).
 */
export function openCards(state: ScheduleState | null | undefined, open: (f: FeatureId) => boolean = () => true): ScheduleCard[] {
  return (state?.cards ?? [])
    .filter((c) => !c.tried && !c.closed && !c.ignored)
    .filter((c) => {
      const w = c.id in NEED_CARDS ? NEED_CARDS[c.id as NeedCardId].waits : undefined;
      return !w || open(w);
    })
    .sort((a, b) => a.at - b.at);
}
